import { useEffect, useRef, useState } from "react";
import { LayoutGrid, ChevronRight } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";

export const SectionNav = ({ tabs, activeTab, onChange, testIdPrefix = "tab" }) => {
  const scrollRef = useRef(null);
  const chipRefs = useRef({});
  const [open, setOpen] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const check = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    check();
    const el = scrollRef.current;
    el?.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    const t = setTimeout(check, 500);
    return () => {
      el?.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
      clearTimeout(t);
    };
  }, [tabs]);

  useEffect(() => {
    const el = scrollRef.current;
    const chip = chipRefs.current[activeTab];
    if (el && chip) {
      el.scrollTo({ left: chip.offsetLeft - el.clientWidth / 2 + chip.clientWidth / 2, behavior: "smooth" });
    }
  }, [activeTab]);

  const select = (id) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1 min-w-0">
        <div ref={scrollRef} className="flex gap-2 overflow-x-auto scrollbar-hide py-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                ref={(node) => { chipRefs.current[tab.id] = node; }}
                onClick={() => select(tab.id)}
                className={`flex items-center gap-1.5 px-3.5 h-10 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all shrink-0 ${
                  active
                    ? `bg-gradient-to-r ${tab.color} text-[#1B2A4A] shadow-lg`
                    : "bg-[#EDF1F8] text-[#2E3B57] hover:text-[#1B2A4A] border border-[#DCE4F0]"
                }`}
                data-testid={`${testIdPrefix}-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        {canRight && (
          <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none z-10 flex items-center justify-end pr-0.5">
            <span className="h-6 w-6 rounded-full bg-white shadow-md border border-[#C9D4E6] flex items-center justify-center">
              <ChevronRight className="h-4 w-4 text-[#1B2A4A]" />
            </span>
          </div>
        )}
      </div>
      <button
        onClick={() => setOpen(true)}
        className="shrink-0 h-10 px-3 rounded-xl bg-[#1B2A4A] text-white flex items-center gap-1.5 text-xs font-semibold shadow-md active:scale-95 transition-transform"
        data-testid="all-sections-trigger"
        aria-label="View all sections"
      >
        <LayoutGrid className="h-4 w-4" />
        <span className="hidden xs:inline sm:inline">All</span>
      </button>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="bg-white border-t border-[#C9D4E6] max-h-[85vh]">
          <DrawerTitle className="text-center text-base font-bold text-[#1B2A4A] py-2">
            All Sections
          </DrawerTitle>
          <div className="grid grid-cols-2 gap-2.5 p-4 pb-8 overflow-y-auto" data-testid="all-sections-grid">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => select(tab.id)}
                  className={`flex items-center gap-2.5 px-3.5 h-14 rounded-xl text-sm font-semibold text-left transition-all active:scale-95 ${
                    active
                      ? "bg-[#1B2A4A] text-white shadow-lg"
                      : "bg-[#EDF1F8] text-[#1B2A4A] border border-[#DCE4F0]"
                  }`}
                  data-testid={`section-grid-${tab.id}`}
                >
                  <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-white/15" : "bg-white shadow-sm"}`}>
                    <Icon className={`h-4 w-4 ${active ? "text-[#D4A017]" : "text-[#1B2A4A]"}`} />
                  </span>
                  <span className="leading-tight">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default SectionNav;
